<template>
  <div class="login-screen">
    <div class="loader-wrapper">
      <div v-show="isLoading" class="loader"></div>
    </div>
    <div v-if="!isLoading && conferenceExists" class="conference-wrapper">
      <h2 class="header">{{ $t('conference.parking_header') }}</h2>
      <span>{{product.name}}</span>
      <div v-for="zone in product.zones" :key="zone.id" class="item__row item__subtitle">
        <span>{{zone.address}}</span>
      </div>
      <hr />
      
      <br />
      <div class="item__row item__subtitle">
        {{ $t('conference.valid_times') }}
      </div>
      <p v-for="rule in parkingRules()" :key="rule.id">
        {{ `${formatDate(rule.startTime)} - ${formatDate(rule.endTime)}` }}
      </p>
      <br />

      <hr />

      <form v-if="hasFreeSpots && !vehicleAdded" class="form fixed-form">
        <table class="details-table no-col-padding edit-table fixed-table label-col-wide-260">
          <tr class="details-table__row" :class="{'details-table__row_error' : errors.has('vehicleRegNr')}">
            <td class="details-table__col details-table__col_label">
              <label for="vehicleRegNr" class="form__label">{{ $t('conference.vehicle_regnr') }}</label>
            </td>
            <td class="details-table__col">
              <input type="text" class="form__input"
                      v-model="form.vehicleRegNr"
                      name="vehicleRegNr"
                      id="vehicleRegNr"
                      ref="vehicleRegNr"
                      v-validate="'required|min:2|max:255|validVehicleRegNr'"/>
              <tooltip v-show="errors.has('vehicleRegNr')" class="form__validation">{{vehicleAlreadyParking ? $t('conference.already_parking') : $t('conference.invalid_regnr')}}</tooltip>
            </td>
          </tr>

          
        </table>
        <button type="submit" class="buttonset__button btn btn--main" @click.prevent.stop="onSubmit()">
          {{ $t('conference.park_vehicle') }}
        </button>
      </form>
      <div v-else-if="vehicleAdded">
        <h2 v-if="!parkingError" class="header">{{ $t('conference.vehicle_parked', {regnr: successVehicleRegNr}) }}</h2>
        <h2 class="parking-error" v-else>{{ parkingErrorMessage }}</h2>
      </div>
      <span v-else>{{ $t('conference.no_free_spots') }}</span>
    </div>
    <div v-else-if="!isLoading" class="conference-wrapper">
      <h2 class="header">{{ $t('conference.link_invalid') }}</h2>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import Tooltip from 'common/Tooltip';
import moment from 'moment';
import { sanitizeVehicleRegNumber } from '../../utils';

export default {
  components: {
    Tooltip,
  },
  data() {
    return {
      form: {},
      conferenceExists: true,
      isLoading: true,
      vehicleAdded: false,
      successVehicleRegNr: '',
      parkingErrorMessage: '',
    };
  },
  mounted() {
    this.fetchAllData();
  },
  created() {
    this.resetValidators();
    this.createValidators();
  },
  computed: {
    ...mapGetters({
      parkingError: 'parkings/parking/error',
      product: 'products/product/product',
    }),
    freeSpots() {
      return this.product.conferenceParkings ?
        (this.product.total - this.product.conferenceParkings.length) :
        this.product.total;
    },
    hasFreeSpots() {
      return (this.freeSpots > 0);
    },
    vehicleAlreadyParking() {
      return (this.form.vehicleRegNr && this.product.conferenceParkings.indexOf(
        sanitizeVehicleRegNumber(this.form.vehicleRegNr).toUpperCase()) > -1);
    },
  },
  watch: {
    $route: 'fetchAllData',
  },
  methods: {
    fetchAllData() {
      if (this.$route.params.url) {
        this.fetchProduct();
      } else {
        this.$store.commit(`products/product/${mTypes.CLEAR_STATE}`);
      }
    },
    fetchProduct() {
      this.isLoading = true;
      this.$store.dispatch('products/product/fetchConference', { url: this.$route.params.url }).then(() => {
        this.conferenceExists = true;
        this.isLoading = false;
      }).catch(() => {
        this.conferenceExists = false;
        this.isLoading = false;
      });
    },
    parkingRules() {
      return this.product.conferenceRules.filter(this.parkingRulesFilter);
    },
    parkingRulesFilter(rule) {
      return rule.type === 'parking';
    },
    formatDate(momentDate) {
      return momentDate == null ? '*' : moment(momentDate).format('DD.MM.YYYY HH:mm');
    },
    onSubmit() {
      this.$validator.validateAll(
        { vehicleRegNr: this.form.vehicleRegNr },
      )
        .then((success) => {
          if (success) {
            this.$store.dispatch('parkings/parking/saveConference', { vehicleRegNr: this.form.vehicleRegNr, productId: this.product.id })
              .then(() => {
                if (!this.parkingError) {
                  this.successVehicleRegNr = sanitizeVehicleRegNumber(this.form.vehicleRegNr)
                    .toUpperCase();
                } else if (this.parkingError === 'parking_exists') {
                  this.parkingErrorMessage = this.$t('conference.error_vehicle_parking', { regnr: sanitizeVehicleRegNumber(this.form.vehicleRegNr)
                    .toUpperCase() });
                } else if (this.parkingError === 'no_free_spots') {
                  this.parkingErrorMessage = this.$t('conference.error_no_free_spots');
                } else if (this.parkingError === 'valid_product_not_found') {
                  this.parkingErrorMessage = this.$t('conference.error_product_not_found');
                } else if (this.parkingError === 'registration_expired') {
                  this.parkingErrorMessage = this.$t('conference.error_registration_expired');
                }

                this.vehicleAdded = true;
                this.form = {};
              });
          }
        });
    },
    resetValidators() {
      this.$validator.remove('validVehicleRegNr');
      this.$validator.clean();
    },
    createValidators() {
      this.createVehicleRegNrValidator();
    },
    createVehicleRegNrValidator() {
      this.$validator.extend('validVehicleRegNr', {
        getMessage: field => `The vehicle with reg number ${field} is already parking.`,
        validate: (value) => {
          const sanitized = sanitizeVehicleRegNumber(value).toUpperCase();
          return (this.product.conferenceParkings.indexOf(sanitized) < 0);
        },
      });
    },
  },
};
</script>

<style lang="scss">
@import '~styles/app.scss';

  .parking-error {
    color: var(--brand-red);
  }

  .login-screen {

    top: 55px !important;
    bottom: auto !important;
    transform: translate(50%,0) !important;
    margin-bottom: 45px;
    position: absolute !important;
    
    @media screen and (max-height: 540px) {
      margin-top: 40px;
    }

    @media screen and (max-width: 745px) {
      max-width: 400px;
    }

    @media screen and (max-width: 600px) {
      max-width: 300px;
    }

    @media screen and (max-width: 520px) {
      max-width: 100%;
    }

    .conference-wrapper {
      padding: 20px 60px;

      @media screen and (max-width: 625px) {
        padding: 10px 30px!important;
      }

      @media screen and (max-width: 359px) {
        padding: 10px 15px!important;
      }


      .header {
        font-weight: 400;
        padding: 0 0 5px 0;

        @media screen and (max-height: 740px) { /* was 650px */
          margin: 10px 0;
        }

      }

    }
  }
</style>



// WEBPACK FOOTER //
// src/components/parkings/ConferenceParkingView.vue